import { useState } from "react";
import { CheckCircle2, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader, SiteFooter } from "@/pages/site/SiteLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADES = [
  { value: "first_aider", label: "First Aider / Medical" },
  { value: "level_3", label: "Level 3" },
  { value: "level_4", label: "Level 4" },
  { value: "technician", label: "Technician (EMT)" },
  { value: "paramedic", label: "Paramedic" },
  { value: "doctor", label: "Doctor" },
];

const DOC_SLOTS = [
  { kind: "photo", label: "A photo of you", hint: "A clear head & shoulders picture", testid: "join-doc-photo" },
  { kind: "dbs", label: "Copy of your DBS", hint: "Photo or PDF of the certificate", testid: "join-doc-dbs" },
  { kind: "licence", label: "Copy of your driving licence", hint: "Photo or PDF", testid: "join-doc-licence" },
  { kind: "certificate", label: "Copies of your qualifications / certificates", hint: "First-aid, FREC, medical qualifications — upload as many as you have", testid: "join-doc-certificate" },
  { kind: "other", label: "Anything else", hint: "Any other document you want us to have", testid: "join-doc-other" },
];

const inputCls = "h-12 rounded-lg border border-slate-600 px-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00D2C2] bg-[#1A232E] w-full";

export default function StaffRegister() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", grade: "first_aider", has_kit: "" });
  const [regKey, setRegKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploaded, setUploaded] = useState({});
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`${API}/public/staff-register`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Something went wrong — please try again");
      setRegKey(d.key);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/public/staff-register/${encodeURIComponent(regKey)}/document?kind=${kind}`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed — please try again");
      setUploaded((u) => ({ ...u, [kind]: (u[kind] || 0) + 1 }));
      toast.success("Uploaded — thank you");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B0F14]" data-testid="join-done">
        <SiteHeader />
        <div className="grid place-items-center p-4 py-20">
        <div className="bg-[#121820] rounded-2xl border border-slate-700 p-8 max-w-md w-full text-center">
          <CheckCircle2 className="h-12 w-12 text-[#007F3B] mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-bold text-white mb-2">All done — thank you</h1>
          <p className="text-sm text-slate-300">Wales Event Medical Services has your details{Object.keys(uploaded).length ? " and documents" : ""}. We'll be in touch about shifts.</p>
        </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F14]" data-testid="join-page">
      <SiteHeader />

      <main className="max-w-xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="text-center pt-2">
          <h1 className="font-heading text-2xl font-bold text-white">Join The Team</h1>
          <p className="text-sm text-slate-400 mt-1">Fill this in and we'll add you to the Wales Event Medical Services staff list.</p>
        </div>

        {!regKey ? (
          <form onSubmit={submit} className="bg-[#121820] rounded-2xl border border-slate-700 p-5 space-y-3" data-testid="join-form">
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="First name" value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                data-testid="join-first-name" className={inputCls} />
              <input required placeholder="Last name" value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                data-testid="join-last-name" className={inputCls} />
            </div>
            <input required type="email" placeholder="Email address" value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="join-email" className={inputCls} />
            <input type="tel" placeholder="Phone number" value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              data-testid="join-phone" className={inputCls} />
            <select value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
              data-testid="join-grade" className={inputCls}>
              {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <div className="rounded-lg border border-slate-600 bg-[#121820] px-3 py-2.5" data-testid="join-kit">
              <p className="text-sm text-slate-300 font-semibold mb-1.5">Do you have your own kit?</p>
              <div className="flex gap-2">
                {[{ v: "yes", label: "Yes" }, { v: "no", label: "No" }].map((o) => (
                  <button type="button" key={o.v} onClick={() => setForm((f) => ({ ...f, has_kit: o.v }))}
                    data-testid={`join-kit-${o.v}`} aria-pressed={form.has_kit === o.v}
                    className={`flex-1 h-11 rounded-lg border text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors ${form.has_kit === o.v ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-600 text-slate-300 hover:border-[#005EB8]"}`}>
                    {form.has_kit === o.v && <CheckCircle2 className="h-4 w-4" />} {o.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="join-submit"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <UserPlus className="h-4 w-4" /> {saving ? "Sending…" : "Send my details"}
            </button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="join-docs">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-[#007F3B] font-semibold text-center">
              Details received — now add your documents (skip any you don't have to hand)
            </div>
            {DOC_SLOTS.map((s) => (
              <label key={s.kind} data-testid={s.testid}
                className={`flex items-center gap-3 bg-[#121820] rounded-xl border p-4 cursor-pointer transition-colors ${uploaded[s.kind] ? "border-emerald-300" : "border-slate-700 hover:border-[#005EB8]"}`}>
                {uploaded[s.kind] ? (
                  <CheckCircle2 className="h-5 w-5 text-[#007F3B] shrink-0" />
                ) : (
                  <Upload className="h-5 w-5 text-[#005EB8] shrink-0" />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-white">{s.label}{uploaded[s.kind] > 1 ? ` (${uploaded[s.kind]} uploaded)` : uploaded[s.kind] ? " — uploaded" : ""}</span>
                  <span className="block text-xs text-slate-400">{busy === s.kind ? "Uploading…" : s.hint}</span>
                </span>
                <input type="file" className="hidden" disabled={busy !== ""}
                  accept={s.kind === "photo" ? "image/jpeg,image/png,image/webp" : "image/*,application/pdf"}
                  onChange={(e) => { upload(s.kind, e.target.files[0]); e.target.value = ""; }} />
              </label>
            ))}
            <button onClick={() => setDone(true)} data-testid="join-finish"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm">
              Finish
            </button>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
